import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { ComplianceCheck, CheckStatus } from './ComplianceCheck';
interface CheckItem {
  title: string;
  description: string;
  status: CheckStatus;
  remediation?: string;
  location?: {
    section: string;
    excerpt?: string;
  };
  pendingData?: {
    type: 'nmls' | 'rateSheet' | 'stateLicenses';
    detectedValue?: string;
  };
}
interface CheckGroupProps {
  title: string;
  checks: CheckItem[];
  defaultExpanded?: boolean;
  onDataSubmit?: (checkIndex: number, value: string) => void;
}
export function CheckGroup({
  title,
  checks,
  defaultExpanded = false,
  onDataSubmit
}: CheckGroupProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const passedCount = checks.filter(c => c.status === 'passed').length;
  const failedCount = checks.filter(c => c.status === 'failed').length;
  const warningCount = checks.filter(c => c.status === 'warning').length;
  const pendingCount = checks.filter(c => c.status === 'pending').length;
  const totalCount = checks.length;
  const completedCount = passedCount + failedCount + warningCount;
  const passRate = totalCount > 0 ? Math.round(passedCount / totalCount * 100) : 0;
  const groupStatus: CheckStatus = failedCount > 0 ? 'failed' : warningCount > 0 ? 'warning' : pendingCount > 0 ? 'pending' : 'passed';
  const statusConfig = {
    passed: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-100',
      text: 'text-emerald-600',
      progressBg: 'bg-emerald-500',
      label: `${passRate}% Pass`
    },
    failed: {
      bg: 'bg-red-50',
      border: 'border-red-100',
      text: 'text-red-600',
      progressBg: 'bg-red-500',
      label: `${failedCount} Failed`
    },
    warning: {
      bg: 'bg-amber-50',
      border: 'border-amber-100',
      text: 'text-amber-600',
      progressBg: 'bg-amber-500',
      label: `${warningCount} Warning${warningCount > 1 ? 's' : ''}`
    },
    pending: {
      bg: 'bg-slate-100',
      border: 'border-slate-200',
      text: 'text-slate-600',
      progressBg: 'bg-slate-400',
      label: `${pendingCount} Pending`
    }
  };
  const config = statusConfig[groupStatus];
  return <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-4" data-id="element-1">
      <button onClick={() => setIsExpanded(!isExpanded)} className="w-full flex items-center justify-between p-4 bg-slate-50/50 hover:bg-slate-50 transition-colors border-b border-slate-100" data-id="element-2">
        <div className="flex items-center gap-3" data-id="element-3">
          <div className={`
            p-1 rounded-md bg-slate-100 text-slate-500 transition-transform duration-200
            ${isExpanded ? 'rotate-90' : ''}
          `} data-id="element-4">
            <ChevronRight className="w-4 h-4" data-id="element-5" />
          </div>
          <div className="text-left" data-id="element-6">
            <h3 className="text-base font-semibold text-slate-900" data-id="element-7">{title}</h3>
            <p className="text-xs text-slate-500 mt-0.5" data-id="element-8">
              {passedCount}/{totalCount} checks passed
              {failedCount > 0 && <span className="text-red-600 font-medium ml-1" data-id="element-9">
                  • {failedCount} failed
                </span>}
              {warningCount > 0 && <span className="text-amber-600 font-medium ml-1" data-id="element-10">
                  • {warningCount} warning{warningCount > 1 ? 's' : ''}
                </span>}
              {pendingCount > 0 && <span className="text-slate-600 font-medium ml-1" data-id="element-11">
                  • {pendingCount} pending
                </span>}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2" data-id="element-12">
          <div className="h-1.5 w-24 bg-slate-100 rounded-full overflow-hidden hidden sm:block" data-id="element-13">
            <div className={`h-full ${config.progressBg} rounded-full transition-all duration-300`} style={{
            width: `${passRate}%`
          }} data-id="element-14" />
          </div>
          <span className={`text-xs font-medium ${config.text} ${config.bg} px-2 py-1 rounded-full border ${config.border}`} data-id="element-15">
            {config.label}
          </span>
        </div>
      </button>

      <AnimatePresence initial={false} data-id="element-16">
        {isExpanded && <motion.div initial={{
        height: 0
      }} animate={{
        height: 'auto'
      }} exit={{
        height: 0
      }} transition={{
        duration: 0.3,
        ease: [0.04, 0.62, 0.23, 0.98]
      }} className="overflow-hidden" data-id="element-17">
            <div className="px-4" data-id="element-18">
              {checks.map((check, index) => <ComplianceCheck key={index} title={check.title} description={check.description} status={check.status} remediation={check.remediation} location={check.location} pendingData={check.pendingData} onDataSubmit={onDataSubmit ? value => onDataSubmit(index, value) : undefined} data-id="element-19" />)}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}